import React, { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, LayoutDashboard, FolderKanban, Users, MessageSquare, Newspaper, Inbox, LogOut, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/projects', icon: FolderKanban, label: 'Projects' },
    { path: '/admin/team', icon: Users, label: 'Team' },
    { path: '/admin/testimonials', icon: MessageSquare, label: 'Testimonials' },
    { path: '/admin/news-media', icon: Newspaper, label: 'News & Media' },
    { path: '/admin/social-activities', icon: Camera, label: 'Social Activities' },
    { path: '/admin/inquiries', icon: Inbox, label: 'Inquiries' }
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <aside className="w-64 bg-stone-900 text-white flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-stone-800">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-white rounded-xl px-2 py-1 shadow-sm inline-flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/57kg0gau_DBK%20LOGO%20Final.png" 
                alt="DBK Group Of Companies" 
                className="h-8 w-auto"
              />
            </div>
          </div>
          <p className="text-lg font-playfair font-bold mb-1">DBK Group Of Companies</p>
          <p className="text-stone-400 text-sm">Admin Panel</p>
        </div>

        <nav className="flex-1 p-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded mb-2 transition-colors ${
                  isActive ? 'bg-emerald-900 text-white' : 'text-stone-400 hover:bg-stone-800 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-stone-800">
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-stone-400 hover:text-white hover:bg-stone-800"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-8">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;